import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GuildTreasuryResponse } from '@/lib/api';

interface UseGuildTreasuryOptions {
  /** Only fetch when the guild is an earning guild and user is a member */
  enabled?: boolean;
}

interface UseGuildTreasuryReturn {
  treasury: GuildTreasuryResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useGuildTreasury(
  guildId: string | undefined,
  options?: UseGuildTreasuryOptions,
): UseGuildTreasuryReturn {
  const enabled = options?.enabled ?? true;
  const [treasury, setTreasury] = useState<GuildTreasuryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchTreasury = useCallback(async () => {
    if (!guildId || !enabled) return;
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getGuildTreasury(guildId);
      if (fetchIdRef.current !== id) return;
      setTreasury(result);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load treasury');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [guildId, enabled]);

  useEffect(() => {
    if (enabled) {
      fetchTreasury();
    }
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchTreasury, enabled]);

  return {
    treasury,
    loading,
    error,
    refresh: fetchTreasury,
  };
}
